import { createContext, useContext, useState } from 'react';
import { sortRows } from 'ui/table';
import { ASCENDING } from 'constants/sortDirections';
import { DEFAULT_PAGE_SIZES } from './constants';
import { Row, FilterByValues } from './types';

type SimpleIndexContextValue = {
  allRecords: any[];
  hasRecords: boolean;
  setAllRecords: (r: any[]) => void;
  currentRecords: any[];
  filteredRecords: any[];
  activeColumns: any[];
  setActiveColumns: (c: any[]) => void;
  filterByValues: FilterByValues;
  setFilterByValues: (filterByValues: FilterByValues) => void;
  toggleFilterByValue: (colValue: string, value: string) => void;
  query: string;
  setQuery: (value: string) => void;
  sortedBy: string | undefined;
  setSortedBy: (value: string) => void;
  sortDirection: string;
  setSortDirection: (value: string) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  pageSizes: number[];
};

const initialValue = {} as SimpleIndexContextValue;

export const SimpleIndexContext = createContext(initialValue);

type Props = {
  queryByFn?: (query: string, row: Row) => boolean;
  children: React.ReactNode;
};

export const SimpleIndexContextProvider = ({ queryByFn, children }: Props) => {
  const [allRecords, setAllRecords] = useState<any[]>([]);
  const [query, setQuery] = useState('');
  const [filterByValues, setFilterByValues] = useState<FilterByValues>({});
  const filterByColValues = Object.keys(filterByValues);
  const [sortedBy, setSortedBy] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState(ASCENDING);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZES[0]);
  const [activeColumns, setActiveColumns] = useState<any[]>([]);

  const setQueryWithPageReset = (q: string) => {
    /**
     * Reset page on query, to ensure any records are seen.
     */
    setCurrentPage(1);
    setQuery(q);
  };

  const toggleFilterByValue = (colValue: string, value: string) => {
    let nextRowValues = [];
    const currentRowValues = filterByValues[colValue];
    if (currentRowValues) {
      nextRowValues = currentRowValues.includes(value)
        ? currentRowValues.filter((v) => v !== value)
        : [...currentRowValues, value];
    } else {
      nextRowValues = [value];
    }
    setFilterByValues({ ...filterByValues, [colValue]: nextRowValues });
  };

  /**
   * If any rows are "pinned", they should be exempted from sorting
   * and pagination.
   */
  const [pinnedRecords, restRecords] = allRecords.reduce(
    (acc, record) => {
      if (record.pinned) {
        acc[0] = [...acc[0], record];
      } else {
        acc[1] = [...acc[1], record];
      }
      return acc;
    },
    [[], []],
  );

  // 1. Sort records
  const sortedRecords: Row[] = sortRows(restRecords, sortedBy as keyof Row, sortDirection);
  // 2. Filter records by search query
  const filteredRecords = sortedRecords.filter((row: Row) => {
    let matchesQuery = true;
    if (query.length > 0 && typeof queryByFn === 'function') {
      matchesQuery = queryByFn(query, row);
    }

    let matchesFilters = true;
    if (
      filterByColValues.length > 0 &&
      Object.values(filterByValues).every((rowValues) => rowValues.length > 0) &&
      Object.entries(filterByValues).every(
        ([colValue, rowValues]: [string, string[]]) => !rowValues.includes(row[colValue] as string),
      )
    ) {
      matchesFilters = false;
    }

    return matchesQuery && matchesFilters;
  });
  // 3. Slice current page of rows
  const currentRowRange = (currentPage - 1) * pageSize;
  const currentRecords = filteredRecords.slice(currentRowRange, currentRowRange + pageSize);

  /**
   * Pinned records are not shown when search is performed.
   */
  const displayedRecords = query ? currentRecords : [...pinnedRecords, ...currentRecords];

  const value = {
    allRecords: restRecords,
    hasRecords: Boolean(allRecords.length),
    currentRecords: displayedRecords,
    filteredRecords,
    setAllRecords,
    activeColumns,
    setActiveColumns,
    filterByValues,
    setFilterByValues,
    toggleFilterByValue,
    query,
    setQuery: setQueryWithPageReset,
    sortedBy,
    setSortedBy,
    sortDirection,
    setSortDirection,
    currentPage,
    setCurrentPage,
    setPageSize,
    pageSize,
    pageSizes: DEFAULT_PAGE_SIZES,
  };

  return <SimpleIndexContext.Provider value={value}>{children}</SimpleIndexContext.Provider>;
};

export const useSimpleIndexContext = () => useContext(SimpleIndexContext);
